import { useState } from "react";
import { DATA_SCOPES } from "@prism/shared/modules";
import { Card, CardHeader, CardBody, DataTable, PageError, PageLoading } from "../components/index.js";
import { Field, Select, FieldLabel, SectionLabel } from "../components/AdminForm.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { timeAgo } from "../lib/time.js";

const ROLE_OPTIONS = [
  { value: "EMPLOYEE", label: "Employee" },
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "ACCOUNTANT", label: "Accountant" },
];

/**
 * Team management (SUPER_ADMIN only). Lists BMG staff (EMPLOYEE + SUPER_ADMIN)
 * and provides a create/edit form with client-scope multi-select and data-scope
 * checkboxes. Creating an employee sends the standard welcome email.
 */
export function AdminTeam() {
  const api = useApiAction();
  const teamApi = useApi("/api/team");
  const clientsApi = useApi("/api/clients");
  const capsApi = useApi("/api/capabilities");
  // DEFAULT-INCLUSIVE on purpose. An existing membership in a now-inactive
  // division must stay visible and marked (spec §7.2); filtering to active-only
  // rows server-side would make it silently vanish from this form while
  // remaining in the database. Active-vs-inactive is derived below, not fetched.
  const divsApi = useApi("/api/divisions");
  const members = teamApi.data?.members || [];
  const clients = clientsApi.data?.clients || [];
  const loading = teamApi.loading || clientsApi.loading || capsApi.loading || divsApi.loading;
  const error = teamApi.error || clientsApi.error || capsApi.error || divsApi.error;
  const [editing, setEditing] = useState(null); // member object, {} for new, or null
  const [notice, setNotice] = useState(null);

  const reload = () => Promise.all([
    teamApi.reload({ silent: true }),
    clientsApi.reload({ silent: true }),
    capsApi.reload({ silent: true }),
    divsApi.reload({ silent: true }),
  ]);

  if (error) return <PageError error={error} onRetry={() => { teamApi.reload(); clientsApi.reload(); capsApi.reload(); divsApi.reload(); }} />;
  if (loading) return <PageLoading />;

  async function resend(m) {
    setNotice(null);
    try {
      const r = await api(`/api/team/${m.id}/resend-welcome`, { method: "POST" });
      setNotice(r.emailSent ? `Welcome email re-sent to ${m.email}.` : `Welcome link regenerated for ${m.email}${r.welcomeUrl ? `: ${r.welcomeUrl}` : "."}`);
    } catch (e) {
      setNotice(e.message);
    }
  }

  return (
    <div className="space-y-5 max-w-[1100px]">
      <Card>
        <CardHeader
          title="Team"
          subtitle={`${members.length} BMG team ${members.length === 1 ? "member" : "members"}`}
          right={
            <button
              type="button"
              onClick={() => { setNotice(null); setEditing({}); }}
              className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity"
            >
              + Add Employee
            </button>
          }
        />
        {notice && <div className="px-5 py-2 text-[12px] text-text-secondary">{notice}</div>}
        {members.length > 0 && (
          <DataTable
            columns={[
              { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
              { key: "email", label: "Email", render: (r) => <span className="text-text-muted">{r.email}</span> },
              { key: "role", label: "Role", render: (r) => <span className="text-[11px] font-mono">{r.role}</span> },
              { key: "jobTitle", label: "Job Title", render: (r) => r.jobTitle || <span className="text-text-muted">—</span> },
              { key: "capabilities", label: "Capabilities", render: (r) => r.capabilities?.length ? r.capabilities.map((c) => c.name).join(", ") : <span className="text-text-muted">—</span> },
              { key: "status", label: "Status", render: (r) => r.status === "INACTIVE"
                ? <span className="text-[11px] font-semibold text-text-muted">Inactive</span>
                : <span className="text-[11px] font-semibold text-positive">Active</span> },
              { key: "clientScopeCount", label: "Clients", align: "right", render: (r) => r.role === "SUPER_ADMIN" ? "all" : r.role === "ACCOUNTANT" ? "billing" : r.clientScopeCount },
              { key: "lastLogin", label: "Last Login", align: "right", render: (r) => (r.lastLogin ? timeAgo(r.lastLogin) : "never") },
              {
                key: "actions", label: "", align: "right",
                render: (r) => (
                  <span className="inline-flex gap-3 justify-end whitespace-nowrap">
                    <button type="button" onClick={() => { setNotice(null); setEditing(r); }} className="text-[12px] text-accent hover:underline">Edit</button>
                    <button type="button" onClick={() => resend(r)} className="text-[12px] text-text-secondary hover:text-text-primary">Resend</button>
                  </span>
                ),
              },
            ]}
            rows={members}
          />
        )}
        {members.length === 0 && (
          <div className="px-5 py-10 text-center text-[13px] text-text-muted">No team members yet.</div>
        )}
      </Card>

      {editing && (
        <EmployeeForm
          key={editing.id || "new"}
          member={editing}
          clients={clients}
          capabilities={capsApi.data?.capabilities || []}
          divisions={divsApi.data?.divisions || []}
          onCancel={() => setEditing(null)}
          onDone={async (msg) => { setEditing(null); setNotice(msg || null); await reload(); }}
        />
      )}
    </div>
  );
}

function EmployeeForm({ member, clients, capabilities, divisions, onCancel, onDone }) {
  const api = useApiAction();
  const isEdit = !!member.id;
  const [form, setForm] = useState({
    name: member.name || "",
    email: member.email || "",
    jobTitle: member.jobTitle || "",
    role: member.role || "EMPLOYEE",
    status: member.status || "ACTIVE",
    clientScopes: member.clientScopes || [],
    dataScopes: member.dataScopes || [],
    divisionIds: member.divisionIds || [],
    capabilityIds: (member.capabilities || []).map((c) => c.id),
  });
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleScope = (id) =>
    set({ clientScopes: form.clientScopes.includes(id) ? form.clientScopes.filter((x) => x !== id) : [...form.clientScopes, id] });
  const toggleData = (s) =>
    set({ dataScopes: form.dataScopes.includes(s) ? form.dataScopes.filter((x) => x !== s) : [...form.dataScopes, s] });
  const toggleId = (key, id) =>
    set({ [key]: form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id] });

  // Active divisions are the assignable choices. An INACTIVE division appears
  // only when this person already holds it: the membership stays visible,
  // stays preserved on save, and can be removed — but a deactivated brand is
  // never newly assignable to someone who doesn't already have it.
  const activeDivisions = divisions.filter((d) => d.isActive);
  const heldInactiveDivisions = divisions.filter((d) => !d.isActive && form.divisionIds.includes(d.id));
  const selectableDivisions = [...activeDivisions, ...heldInactiveDivisions];

  const filtered = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || (c.slug || "").includes(q);
  });

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const noScopes = form.role === "SUPER_ADMIN" || form.role === "ACCOUNTANT";
      const body = {
        name: form.name,
        jobTitle: form.jobTitle || null,
        role: form.role,
        clientScopes: noScopes ? [] : form.clientScopes,
        dataScopes: noScopes ? [] : form.dataScopes,
        // Membership and capabilities apply to every staff role — they describe
        // who the person is and what work they do, not what they may access.
        divisionIds: form.divisionIds,
        capabilityIds: form.capabilityIds,
        ...(isEdit ? { status: form.status } : {}),
      };
      let r;
      if (isEdit) {
        r = await api(`/api/team/${member.id}`, { method: "PUT", body: JSON.stringify(body) });
        onDone(`Updated ${form.name}.`);
      } else {
        r = await api("/api/team", { method: "POST", body: JSON.stringify({ ...body, email: form.email }) });
        const msg = r.emailSent
          ? `Invited ${form.name} — welcome email sent.`
          : `Invited ${form.name}${r.welcomeUrl ? ` — welcome link: ${r.welcomeUrl}` : "."}`;
        onDone(msg);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const noScopes = form.role === "SUPER_ADMIN" || form.role === "ACCOUNTANT";

  return (
    <Card className="px-6 py-5">
      <SectionLabel>{isEdit ? `Edit — ${member.name}` : "New Team Member"}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 max-w-[720px]">
        <Field label="Name" required value={form.name} onChange={(v) => set({ name: v })} />
        {isEdit
          ? <div><FieldLabel>Email</FieldLabel><div className="h-9 px-3 flex items-center text-[13px] text-text-muted">{form.email}</div></div>
          : <Field label="Email" type="email" required value={form.email} onChange={(v) => set({ email: v })} />}
        <Field label="Job Title" placeholder="Social Media Coordinator" value={form.jobTitle} onChange={(v) => set({ jobTitle: v })} />
        <Select label="Role" value={form.role} onChange={(v) => set({ role: v })} options={ROLE_OPTIONS} />
      </div>
      <p className="mt-1 text-[11px] text-text-muted">Job Title is descriptive only — it does not affect permissions.</p>
      {isEdit && (
        <div className="mt-4">
          <Select
            label="Status"
            value={form.status}
            onChange={(v) => set({ status: v })}
            options={[{ value: "ACTIVE", label: "Active" }, { value: "INACTIVE", label: "Inactive" }]}
          />
          <p className="mt-1 text-[11px] text-text-muted">
            Inactive blocks sign-in and hides this person from new assignments. Their existing work stays assigned to them.
          </p>
        </div>
      )}

      <div className="mt-5">
        <SectionLabel>Capabilities ({form.capabilityIds.length} selected)</SectionLabel>
        <p className="mb-2 text-[11px] text-text-muted">What this person can work on. Descriptive — does not grant access.</p>
        <div className="flex flex-wrap gap-3">
          {capabilities.length === 0 && <span className="text-[12px] text-text-muted">None configured yet — add them under Organization.</span>}
          {capabilities.map((c) => (
            <label key={c.id} className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary">
              <input type="checkbox" checked={form.capabilityIds.includes(c.id)} onChange={() => toggleId("capabilityIds", c.id)} className="accent-accent" />
              {c.name}
            </label>
          ))}
        </div>
      </div>

      {/* Change #1: a single-brand agency is never shown a brand picker. The
          condition is the ACTIVE division count — but the section still appears
          when this person holds an inactive membership that must stay visible. */}
      {(activeDivisions.length >= 2 || heldInactiveDivisions.length > 0) && (
        <div className="mt-5">
          <SectionLabel>Divisions ({form.divisionIds.length} selected)</SectionLabel>
          <p className="mb-2 text-[11px] text-text-muted">Which brands this person works across.</p>
          <div className="flex flex-wrap gap-3">
            {selectableDivisions.map((d) => (
              <label key={d.id} className={`inline-flex items-center gap-1.5 text-[12px] ${d.isActive ? "text-text-secondary" : "text-text-muted"}`}>
                <input type="checkbox" checked={form.divisionIds.includes(d.id)} onChange={() => toggleId("divisionIds", d.id)} className="accent-accent" />
                {d.name}{d.isActive ? "" : " (inactive)"}
              </label>
            ))}
          </div>
          {heldInactiveDivisions.length > 0 && (
            <p className="mt-2 text-[11px] text-text-muted">
              An inactive brand is shown because this person is still a member. Unchecking it removes the membership; it cannot be added back while the brand is inactive. Cancel without saving to restore it.
            </p>
          )}
        </div>
      )}

      {!noScopes && (
        <>
          <div className="mt-5">
            <SectionLabel>Data Scopes</SectionLabel>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex items-center gap-1.5 text-[12px] text-text-muted">
                <input type="checkbox" checked disabled className="accent-accent" /> GA4 (always)
              </label>
              {DATA_SCOPES.map((s) => (
                <label key={s} className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary capitalize">
                  <input type="checkbox" checked={form.dataScopes.includes(s)} onChange={() => toggleData(s)} className="accent-accent" />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <SectionLabel>Client Scope ({form.clientScopes.length} selected)</SectionLabel>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients by name…"
              className="w-full max-w-[360px] h-8 px-3 mb-2 rounded border border-border-default text-[12px] bg-surface focus:outline-none focus:border-accent"
            />
            <div className="max-h-[220px] overflow-y-auto border border-border-light rounded divide-y divide-border-light">
              {filtered.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-text-secondary hover:bg-app-bg cursor-pointer">
                  <input type="checkbox" checked={form.clientScopes.includes(c.id)} onChange={() => toggleScope(c.id)} className="accent-accent" />
                  <span className="font-medium text-text-primary">{c.name}</span>
                  {c.division?.name && <span className="text-text-muted">· {c.division.name}</span>}
                </label>
              ))}
              {filtered.length === 0 && <div className="px-3 py-2 text-[12px] text-text-muted">No clients match.</div>}
            </div>
          </div>
        </>
      )}

      {err && <div className="text-[12px] text-negative mt-3">{err}</div>}
      <div className="flex items-center gap-2 mt-5">
        <button type="button" disabled={busy || !form.name || (!isEdit && !form.email)} onClick={save}
          className="h-9 px-4 rounded bg-accent text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? "Saving…" : isEdit ? "Save Changes" : "Create & Send Welcome"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}
          className="h-9 px-4 rounded border border-border-default text-[13px] text-text-secondary hover:bg-[var(--color-border-light)] disabled:opacity-50">
          Cancel
        </button>
      </div>
    </Card>
  );
}
